import { apiRequest } from "./apiClient";
import type {
  AddRfpQuestionCommentRequest,
  AddRfpQuestionReviewerRequest,
  BulkAssignRfpQuestionsRequest,
  CreateRfpQuestionRequest,
  FetchRfpsParams,
  RfpCreatedQuestion,
  RfpFullResponse,
  RfpOwnerUsersResponse,
  RfpPresenceHeartbeatResponse,
  RfpQuestionFindAnswersResponse,
  RfpQuestionDiscussionResponse,
  RfpSyncPendingResponse,
  RfpsResponse,
  SelectRfpQuestionAnswerRequest,
  UploadKbDocumentParams,
  UpdateRfpQuestionAnswerRequest,
  UploadRfpParams,
} from "../types/rfp";

export type ExportRfpFileResult = {
  readonly blob: Blob;
  readonly fileName: string;
};

const extractFileNameFromDisposition = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;

  const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FileNameMatch?.[1]) {
    try {
      return decodeURIComponent(utf8FileNameMatch[1].trim().replace(/^"(.*)"$/, "$1"));
    } catch {
      return utf8FileNameMatch[1].trim().replace(/^"(.*)"$/, "$1");
    }
  }

  const plainFileNameMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (!plainFileNameMatch?.[1]) return null;
  return plainFileNameMatch[1].trim().replace(/^"(.*)"$/, "$1");
};

export const fetchRfps = async (params: FetchRfpsParams): Promise<RfpsResponse> => {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page));
  sp.set("page_size", String(params.page_size));
  sp.set("sort_by", params.sort_by);
  sp.set("sort_dir", params.sort_dir);

  if (params.search?.trim()) {
    sp.set("search", params.search.trim());
  }

  if (params.status) {
    sp.set("status", params.status);
  }

  const res = await apiRequest(`/rfps?${sp.toString()}`);
  return (await res.json()) as RfpsResponse;
};

export const fetchRfpFull = async (rfpId: string): Promise<RfpFullResponse> => {
  const res = await apiRequest(`/rfps/${encodeURIComponent(rfpId)}/full`);
  return (await res.json()) as RfpFullResponse;
};

export const fetchRfpUsers = async (): Promise<RfpOwnerUsersResponse> => {
  const res = await apiRequest("/rfps/users");
  return (await res.json()) as RfpOwnerUsersResponse;
};

export const heartbeatRfpPresence = async (
  rfpId: string,
): Promise<RfpPresenceHeartbeatResponse> => {
  const res = await apiRequest("/presence/heartbeat", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      context_type: "rfp",
      context_id: rfpId,
      is_editing: false,
    }),
  });
  return (await res.json()) as RfpPresenceHeartbeatResponse;
};

export const uploadRfp = async ({ file, dueDate, ownerId }: UploadRfpParams): Promise<void> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("due_date", dueDate.trim());
  formData.append("owner_id", ownerId.trim());

  await apiRequest("/rfps/upload", {
    method: "POST",
    body: formData,
  });
};

export const uploadKbDocument = async ({ file }: UploadKbDocumentParams): Promise<void> => {
  const formData = new FormData();
  formData.append("file", file);

  await apiRequest("/kb/documents/upload", {
    method: "POST",
    body: formData,
  });
};

export const fetchRfpQuestionDiscussion = async (
  rfpId: string,
  questionId: string,
): Promise<RfpQuestionDiscussionResponse> => {
  const res = await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/discussion`,
  );
  return (await res.json()) as RfpQuestionDiscussionResponse;
};

export const fetchRfpQuestionFindAnswers = async (
  rfpId: string,
  questionId: string,
  topN = 5,
): Promise<RfpQuestionFindAnswersResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set("top_n", String(topN));
  const res = await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/find-answers?${searchParams.toString()}`,
  );
  return (await res.json()) as RfpQuestionFindAnswersResponse;
};

export const addRfpQuestionReviewer = async (
  rfpId: string,
  questionId: string,
  payload: AddRfpQuestionReviewerRequest,
): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/reviewers`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
};

export const addRfpQuestionComment = async (
  rfpId: string,
  questionId: string,
  payload: AddRfpQuestionCommentRequest,
): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/comments`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
};

export const removeRfpQuestionReviewer = async (
  rfpId: string,
  questionId: string,
  userId: string,
): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/reviewers/${encodeURIComponent(userId)}`,
    {
      method: "DELETE",
    },
  );
};

export const updateRfpQuestionAnswer = async (
  rfpId: string,
  questionId: string,
  payload: UpdateRfpQuestionAnswerRequest,
): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/answer`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
};

export const selectRfpQuestionAnswer = async (
  rfpId: string,
  questionId: string,
  payload: SelectRfpQuestionAnswerRequest,
): Promise<void> => {

  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/select`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ selected_answer: payload.selected_answer }),
    },
  );
};

export const addRfpQuestionToKb = async (rfpId: string, questionId: string): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/add-to-kb`,
    {
      method: "POST",
    },
  );
};

export const deselectRfpQuestionAnswer = async (
  rfpId: string,
  questionId: string,
): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/deselect`,
    {
      method: "POST",
    },
  );
};

export const lockRfpQuestion = async (rfpId: string, questionId: string): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/lock`,
    {
      method: "POST",
    },
  );
};

export const unlockRfpQuestion = async (rfpId: string, questionId: string): Promise<void> => {
  await apiRequest(
    `/rfps/${encodeURIComponent(rfpId)}/questions/${encodeURIComponent(questionId)}/unlock`,
    {
      method: "POST",
    },
  );
};

export const bulkAssignRfpQuestions = async (
  rfpId: string,
  payload: BulkAssignRfpQuestionsRequest,
): Promise<void> => {
  await apiRequest(`/rfps/${encodeURIComponent(rfpId)}/bulk-assign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
};

export const createRfpQuestion = async (
  rfpId: string,
  payload: CreateRfpQuestionRequest,
): Promise<RfpCreatedQuestion> => {
  const res = await apiRequest(`/rfps/${encodeURIComponent(rfpId)}/questions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as RfpCreatedQuestion;
};

export const syncRfpPendingQuestions = async (rfpId: string): Promise<RfpSyncPendingResponse> => {
  const res = await apiRequest(`/rfps/${encodeURIComponent(rfpId)}/sync-pending`, {
    method: "POST",
  });
  return (await res.json()) as RfpSyncPendingResponse;
};

export const exportRfpFile = async (rfpId: string): Promise<ExportRfpFileResult> => {
  const response = await apiRequest(`/rfps/${encodeURIComponent(rfpId)}/export`);
  const blob = await response.blob();
  const fileName =
    extractFileNameFromDisposition(response.headers.get("content-disposition")) ||
    "RFP_Export.xlsx";
  return { blob, fileName };
};

export const downloadKbTemplate = async (): Promise<ExportRfpFileResult> => {
  const response = await apiRequest("/kb/template");
  const blob = await response.blob();
  const fileName =
    extractFileNameFromDisposition(response.headers.get("content-disposition")) ||
    "RFP_Template.xlsx";
  return { blob, fileName };
};
